import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { streamToSSEEvents } from '~/utils/streamingSSE';
import { withAgentRunTelemetry } from '~/telemetry/agentRunTelemetry';

import { AddToastNotification } from '~/constants/mutations';

import auth0Client from '~/plugins/authClient';

import router from '~/router';

const BASE_URL = import.meta.env.VUE_APP_ARCANE_FAST_API_URL;

export default class ArcaneFastApi {
  instance = null;
  token = null;

  constructor({ $auth0, $store }) {
    this.$auth0 = $auth0;
    this.instance = axios.create({
      baseURL: BASE_URL,
    });

    this.instance.interceptors.request.use(
      async (config) => {
        try {
          this.instance.token = await $auth0.getAccessTokenSilently();
          config.headers.Authorization = `Bearer ${this.instance.token}`;
        } catch (error) {
          console.error('Error getting access token:', error);
          await auth0Client.loginWithRedirect({
            appState: { targetUrl: router.currentRoute.value.fullPath },
          });
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        let toastErrorMessage = error.message;
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          toastErrorMessage = detail;
        }
        if (error.response && error.response.status === 403) {
          toastErrorMessage = ` Route ${error.config.url} is forbidden`;
        } else if (error.code === 'ERR_NETWORK') {
          toastErrorMessage = `${error.message} or route ${error.config.url} not found. Check also CORS policy`;
        }

        if (error.response?.status !== 500) {
          $store.commit(`notificationStore/${AddToastNotification}`, {
            type: 'toast',
            title: toastErrorMessage,
            notification: { externalId: uuidv4() },
            createdAt: Date.now(),
          });
        }

        return Promise.reject(error);
      },
    );
  }

  async post(endpoint, data) {
    const { url } = endpoint;
    return this.instance.post(url, data);
  }

  async get(endpoint, data) {
    const { url } = endpoint;
    return this.instance.get(url, data);
  }

  async delete(endpoint, data) {
    const { url } = endpoint;
    return this.instance.delete(url, data);
  }

  async patch(endpoint, data) {
    const { url } = endpoint;
    return this.instance.patch(url, data);
  }

  async put(endpoint, data, opts) {
    const { url } = endpoint;
    return this.instance.put(url, data, opts);
  }

  /**
   * Opens an SSE POST stream and yields parsed events as an async iterable.
   *
   * @param {{ url: string }} endpoint
   *   Endpoint descriptor — `url` is appended to `BASE_URL`.
   *   Known values:
   *   - `fastApiEndpoints.assist.stream(conversationId)`         → `{ url: '/v0/assist/{thread_id}/stream' }`
   *   - `fastApiEndpoints.assist.commandsResume(conversationId)` → `{ url: '/v0/assist/{thread_id}/commands/resume' }`
   *
   * @param {object} data  Request body, serialised as JSON. Shape varies by endpoint:
   *
   *   **`/v0/assist/{thread_id}/stream`**
   *   ```js
   *   {
   *     query:   string,   // plain-text user message
   *     filters: Filter[], // active collection/scope filters
   *   }
   *   ```
   *
   *   **`/v0/assist/{thread_id}/commands/resume`** — `payload.type` discriminates the union:
   *
   *   HITL tool-call response:
   *   ```js
   *   {
   *     payload: {
   *       type: 'hitlResponse',
   *       decisions: Array<
   *         | { type: 'approve' }
   *         | { type: 'reject', message?: string }
   *         | { type: 'edit', edited_action: { name: string, args: object } }
   *       >,
   *     },
   *   }
   *   ```
   *
   *   Image-generation interrupt response:
   *   ```js
   *   {
   *     payload: {
   *       type: 'imageStyleChoicesResponse',
   *       imageGenerationParams: {
   *         width:          number,
   *         height:         number,
   *         brandStyleId:   string,
   *         remixImageId:   string,
   *         numberOfImages: number,
   *       },
   *     },
   *   }
   *   ```
   *
   * @yields {{ event: string, data: object }} Parsed SSE events from the server.
   */
  async *stream(endpoint, data) {
    const token = await this.$auth0.getAccessTokenSilently();
    yield* withAgentRunTelemetry(
      endpoint.url,
      streamToSSEEvents(`${BASE_URL}${endpoint.url}`, {
        method: 'POST',
        headers: {
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      }),
      data?.filters,
    );
  }

  async putKeepAlive(endpoint, data) {
    const { url } = endpoint;

    return await fetch(`${BASE_URL}${url}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${this.instance.token}` },
      body: JSON.stringify(data),
    });
  }
}
